/**
 * commerce-analytics-buffer.ts
 *
 * Internal buffer / transport layer for commerce-analytics.ts.
 * Handles the consent gate, React Native bridge accessor, error reporting,
 * and the polling pump that retries events until both bridge and consent
 * are available (or MAX_WAIT_MS is exceeded).
 *
 * Not part of the public API — import from commerce-analytics instead.
 */

import { notifyError } from "common/utils/app-monitoring/notify";

import type { Ga4EventName } from "./commerce-analytics";

// ─── Constants ────────────────────────────────────────────────────────────────

const POLL_MS = 250;
const MAX_WAIT_MS = 10_000;

// ─── Consent gate (fail-closed) ───────────────────────────────────────────────

/**
 * Returns true ONLY when iubenda has loaded and the user has explicitly
 * granted both "sale" (s) and "advertising" (adv) consent.
 *
 * Decision D11-A: fail-closed — absent/unloaded iubenda → NOT consented.
 * This accepts under-reporting for newly loaded pages as the safer tradeoff
 * over risk of emitting on a non-consented session.
 */
export function hasStatisticsConsent(): boolean {
  try {
    const iub = (window as any)._iub;
    if (!iub || !iub.cs || !iub.cs.api) return false;
    const prefs = iub.cs.api.getPreferences();
    if (!prefs || !prefs.uspr) return false;
    return prefs.uspr.s === true && prefs.uspr.adv === true;
  } catch {
    return false;
  }
}

// ─── Bridge accessor ─────────────────────────────────────────────────────────

export function bridge(): ((event: string, data: unknown) => void) | null {
  const fn = (window as any)?.mobileApp?.publishTracked;
  return typeof fn === "function" ? fn : null;
}

// The actual native transport. `publishTracked` is installed by the mobile-app pack
// and EXISTS before React Native injects `ReactNativeWebView` — and returns false
// (silently dropping) when the real bridge isn't there yet. So readiness must gate on
// the transport itself, not on publishTracked merely being defined.
function rnTransportReady(): boolean {
  return typeof (window as any)?.ReactNativeWebView?.postMessage === "function";
}

// We're plausibly inside the native app (set server-side on mobile layouts, or RN bridge
// present). On normal web this is false, so emissions no-op immediately with no buffering.
function isMobileAppContext(): boolean {
  return (window as any)?.MOBILE_APP === true || rnTransportReady();
}

// Ready to actually deliver: publishTracked installed AND the RN transport injected.
function isBridgeReady(): boolean {
  return bridge() !== null && rnTransportReady();
}

// ─── Error reporting ─────────────────────────────────────────────────────────

function reportError(err: unknown, context: string): void {
  try {
    notifyError(err instanceof Error ? err : new Error(String(err)), {
      context: { source: "commerce-analytics", event: context }
    });
  } catch {
    // notifyError itself threw — silently swallow to never interrupt the caller
  }
}

// ─── Emit (immediate, no buffering) ───────────────────────────────────────────

function emitNow(name: Ga4EventName, params: Record<string, unknown>): void {
  const bridgeFn = bridge();
  if (!bridgeFn) return;
  try {
    // The shell (mobile-app useWebview.ts) destructures `{ method, name, ...payload }`
    // and calls firebaseAnalytics().logEvent(name, payload) — so the GA4 params must be
    // spread FLAT alongside method/name, matching the existing firebaseTrackPurchase fire.
    // (Params named `method`/`name` are therefore unsupported by this bridge contract.)
    bridgeFn("firebaseTrackEvent", { method: "logEvent", name, ...params });
  } catch (err) {
    reportError(err, name);
  }
}

// ─── Buffer + pump ───────────────────────────────────────────────────────────

interface BufferedItem {
  name: Ga4EventName;
  params: Record<string, unknown>;
  enqueuedAt: number;
}

let buffer: BufferedItem[] = [];
let pumpTimer: ReturnType<typeof setInterval> | null = null;

function pump(): void {
  const now = Date.now();
  // Drop items that have been waiting too long (bridge never appeared or no consent)
  buffer = buffer.filter(item => now - item.enqueuedAt < MAX_WAIT_MS);

  if (buffer.length === 0) {
    stopPump();
    return;
  }

  const bridgeFn = bridge();
  const consented = hasStatisticsConsent();

  // Only flush when the native transport is actually injected AND consent is granted.
  // (publishTracked existing is NOT enough — it returns false and drops before RN
  // injects ReactNativeWebView; isBridgeReady gates on the real transport.)
  if (!bridgeFn || !isBridgeReady() || !consented) {
    // If consent was never going to arrive (consent is the blocker and _iub is absent
    // entirely) we still respect MAX_WAIT_MS above. Bridge absence is the typical race.
    return;
  }

  const toFlush = buffer.splice(0);
  stopPump();

  for (const item of toFlush) {
    try {
      bridgeFn("firebaseTrackEvent", { method: "logEvent", name: item.name, ...item.params });
    } catch (err) {
      reportError(err, item.name);
    }
  }
}

function startPump(): void {
  if (pumpTimer !== null) return;
  pumpTimer = setInterval(pump, POLL_MS);
}

function stopPump(): void {
  if (pumpTimer !== null) {
    clearInterval(pumpTimer);
    pumpTimer = null;
  }
}

// ─── Public emit API ─────────────────────────────────────────────────────────

/**
 * Emit a Firebase GA4 event over the React Native bridge.
 * If the bridge is not yet available OR consent is not yet resolved, buffers
 * the event and retries via a polling interval for up to MAX_WAIT_MS.
 * Both bridge-readiness and consent-readiness gate the flush symmetrically —
 * neither causes an immediate drop. The pump drops events only after MAX_WAIT_MS
 * (handles genuine consent denial: consent never becomes true → dropped after 10s).
 * Never throws.
 */
export function emitFirebaseEvent(name: Ga4EventName, params: Record<string, unknown>): void {
  try {
    // Normal web (no native app): no bridge will ever appear. No-op immediately rather
    // than buffer + poll for 10s on every page view.
    if (!isMobileAppContext()) return;

    if (isBridgeReady() && hasStatisticsConsent()) {
      emitNow(name, params);
    } else {
      buffer.push({ name, params, enqueuedAt: Date.now() });
      startPump();
    }
  } catch (err) {
    reportError(err, name);
  }
}

// ─── Test helpers ─────────────────────────────────────────────────────────────

export const __testBuffer = {
  hasStatisticsConsent,
  reset() {
    buffer = [];
    stopPump();
  }
};
